import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Spinner,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { Loader2 } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button as ShadcnButton } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Textarea as ShadcnTextarea } from "@/components/ui/textarea";
import { PineScriptCodeBlock } from "@/components/Reports/PineScriptCodeBlock";
import {
  isPineScriptWithinLimit,
  PINE_SCRIPT_MAX_CHARS,
  PINE_SCRIPT_MAX_CHARS_MESSAGE,
} from "../../constants/securityLimits";
import { useManualPineParse, type PineParseSessionState } from "../../hooks/useManualPineParse";
import { useReportStrategyPineRequirements } from "../../hooks/useReportStrategyPineRequirements";
import type { ReportStrategyPineFields } from "../../types/reportStrategyPine";
import {
  getEffectiveInjectReadiness,
  getEffectiveParseIssues,
  getEffectiveParseStatus,
  getPineIr,
  getRequirementsWarningCatalog,
  scrollTextareaToLine,
} from "../../utils/pineIrUi";
import { extractPineScriptText } from "../../utils/reportStrategyPineUi";
import { StrategyPineEntryConditions } from "./StrategyPineEntryConditions";
import { StrategyPineExecutionModelCallout } from "./StrategyPineExecutionModelCallout";
import { StrategyPineExitHooks } from "./StrategyPineExitHooks";
import { StrategyPineInjectSummary } from "./StrategyPineInjectSummary";
import { StrategyPineParseIssues, StrategyPineParseIssuesAlert } from "./StrategyPineParseIssues";
import { StrategyPineParseStatusBanner } from "./StrategyPineParseStatusBanner";
import { StrategyPineParseSummaryAlert } from "./StrategyPineParseSummaryAlert";
import {
  STRATEGY_PINE_SECTION_DESC_CLASS,
  STRATEGY_PINE_SECTION_LABEL_CLASS,
} from "./StrategyPineSectionCard";
import { STRATEGY_PINE_ALERT_CLASS, StrategyPineAlertIcon } from "./strategyPineAlertUi";
import { StrategyPineStatusBadges } from "./StrategyPineStatusBadges";

export type { PineParseSessionState };

export type StrategyPineEditorProps = {
  value: string;
  onChange: (next: string) => void;
  subtleText: string;
  textColor: string;
  borderColor: string;
  savedFields?: ReportStrategyPineFields | null;
  minH?: string;
  showParseButton?: boolean;
  label?: string;
  textareaId?: string;
  /** Use shadcn Field / Textarea styling (create-strategy dialog). */
  brandFields?: boolean;
  onParseSessionChange?: (session: PineParseSessionState) => void;
};

export function StrategyPineEditor({
  value,
  onChange,
  subtleText,
  textColor,
  borderColor,
  savedFields,
  minH = "220px",
  showParseButton = true,
  label = "Pinescript",
  textareaId = "strategy-pinescript",
  brandFields = false,
  onParseSessionChange,
}: StrategyPineEditorProps): React.ReactElement {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [editingSource, setEditingSource] = useState(false);
  const { data: requirements } = useReportStrategyPineRequirements();
  const warningCatalog = getRequirementsWarningCatalog(requirements);

  const { result, parsedScript, loading, error, parse, isParseCurrent, isStale } =
    useManualPineParse(value);

  useEffect(() => {
    onParseSessionChange?.({
      result,
      parsedScript,
      isParseCurrent,
      loading,
      error,
      isStale,
    });
  }, [
    error,
    isParseCurrent,
    isStale,
    loading,
    onParseSessionChange,
    parsedScript,
    result,
  ]);

  const pineIr = getPineIr(result);
  const entrySites = pineIr?.entry_sites ?? [];
  const exitSites = pineIr?.exit_sites ?? [];

  const parseIssues = useMemo(
    () =>
      isParseCurrent && result
        ? getEffectiveParseIssues(savedFields, result, warningCatalog)
        : [],
    [isParseCurrent, result, savedFields, warningCatalog]
  );

  const displayFields = useMemo((): ReportStrategyPineFields => {
    const useLiveParse = isParseCurrent && result;
    return {
      ...savedFields,
      pine_parse_status: (useLiveParse
        ? result.pine_parse_status
        : getEffectiveParseStatus(savedFields, null)) as ReportStrategyPineFields["pine_parse_status"],
      inject_readiness: (useLiveParse
        ? getEffectiveInjectReadiness(null, result)
        : savedFields?.inject_readiness) as ReportStrategyPineFields["inject_readiness"],
      exit_hook_readiness: (useLiveParse
        ? result.exit_hook_readiness ?? pineIr?.exit_hook_readiness
        : savedFields?.exit_hook_readiness) as ReportStrategyPineFields["exit_hook_readiness"],
    };
  }, [isParseCurrent, pineIr?.exit_hook_readiness, result, savedFields]);

  const goToLine = useCallback((line: number) => {
    if (textareaRef.current) scrollTextareaToLine(textareaRef.current, line);
  }, []);

  const pineOverLimit = Boolean(value.trim()) && !isPineScriptWithinLimit(value);

  const savedScriptText = useMemo(
    () => (savedFields ? extractPineScriptText(savedFields).trim() : ""),
    [savedFields]
  );

  const isMatchingSavedScript = Boolean(
    savedScriptText && value.trim() && value.trim() === savedScriptText
  );

  const hasSavedParseOnRecord = Boolean(savedFields?.pine_parse_status);

  const showingPersistedParse = Boolean(
    hasSavedParseOnRecord && isMatchingSavedScript && value.trim() && !isParseCurrent
  );

  const showParsedUi = isParseCurrent || showingPersistedParse;

  const showParsePanels = Boolean((result && (isParseCurrent || isStale)) || showingPersistedParse);
  const showSavedOnlyHints = Boolean(
    !result && !showingPersistedParse && savedFields?.pine_parse_status
  );
  const showSuccessPath =
    showParsedUi && (result?.pine_ir != null || showingPersistedParse) && displayFields.pine_parse_status !== "failed";

  const pineDescription = (
    <>
      Paste your Pinescript source, then click Parse when you are ready. You must parse before saving
      Pine changes (any parse result is fine). Maximum{" "}
      {PINE_SCRIPT_MAX_CHARS.toLocaleString()} characters.
    </>
  );

  const showStyledPinePreview =
    brandFields && Boolean(value.trim() && !editingSource && showParsedUi);

  const showEmptyPineSlot = brandFields && Boolean(savedFields) && !value.trim() && !editingSource;

  useEffect(() => {
    if (isParseCurrent && !loading) {
      setEditingSource(false);
    }
  }, [isParseCurrent, loading]);

  const parseControls = showParseButton ? (
    <ShadcnButton
      type="button"
      variant="gradient-orange"
      size="sm"
      data-font="primary"
      className="shrink-0"
      onClick={parse}
      disabled={!value.trim() || pineOverLimit || loading}
    >
      {loading ? (
        <>
          <Loader2 className="size-4 animate-spin" aria-hidden />
          Parsing…
        </>
      ) : (
        "Parse"
      )}
    </ShadcnButton>
  ) : null;

  return (
    <VStack align="stretch" spacing={3}>
      {brandFields ? (
        <Field
          data-strategy-pine-editor=""
          className="min-w-0 w-full gap-2"
          style={{ "--strategy-pine-min-h": minH } as React.CSSProperties}
        >
          <div className="flex items-end justify-between gap-3">
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <FieldLabel htmlFor={textareaId} className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
                {label}
              </FieldLabel>
              <FieldDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
                {pineDescription}
              </FieldDescription>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {showEmptyPineSlot ? (
                <ShadcnButton
                  type="button"
                  variant="outline-gray"
                  size="sm"
                  data-font="primary"
                  className="shrink-0"
                  onClick={() => setEditingSource(true)}
                >
                  Add script
                </ShadcnButton>
              ) : showStyledPinePreview ? (
                <ShadcnButton
                  type="button"
                  variant="outline-gray"
                  size="sm"
                  data-font="primary"
                  className="shrink-0"
                  onClick={() => setEditingSource(true)}
                >
                  Edit script
                </ShadcnButton>
              ) : null}
              {parseControls}
            </div>
          </div>
          {showEmptyPineSlot ? (
            <div
              data-strategy-pine-empty=""
              className="min-h-[var(--strategy-pine-min-h,300px)] rounded-[6px] border border-input bg-transparent"
              aria-hidden
            />
          ) : showStyledPinePreview ? (
            <PineScriptCodeBlock
              code={value}
              maxH={minH}
              brandChrome
              chromeSubtitle="Parsed script"
            />
          ) : (
            <ShadcnTextarea
              id={textareaId}
              ref={textareaRef}
              value={value}
              onChange={(event) => onChange(event.target.value)}
              data-strategy-pine-source=""
              placeholder={'// @version=5\nstrategy("My strategy", overlay=true)\n...'}
              className="min-h-[var(--strategy-pine-min-h,300px)] font-mono font-[family-name:var(--font-primary)]"
            />
          )}
        </Field>
      ) : (
        <FormControl>
          <HStack justify="space-between" align="flex-end" mb={1}>
            <FormLabel mb={0} color={textColor}>
              {label}
            </FormLabel>
            <HStack spacing={2}>
              {loading ? (
                <HStack spacing={1}>
                  <Spinner size="xs" />
                  <Text fontSize="xs" color={subtleText}>
                    Parsing…
                  </Text>
                </HStack>
              ) : null}
              {showParseButton ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={parse}
                  isLoading={loading}
                  loadingText="Parsing…"
                  isDisabled={!value.trim() || pineOverLimit}
                >
                  Parse
                </Button>
              ) : null}
            </HStack>
          </HStack>
          <Text fontSize="xs" color={subtleText} mb={2}>
            {pineDescription}
          </Text>
          <Textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            fontFamily="mono"
            fontSize="sm"
            minH={minH}
            placeholder="// @version=5&#10;strategy(&quot;My strategy&quot;, overlay=true)&#10;..."
          />
        </FormControl>
      )}

      {value.trim() && !showParsedUi ? (
        <Alert variant="warning" className={STRATEGY_PINE_ALERT_CLASS}>
          <StrategyPineAlertIcon tone="warning" />
          <AlertDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
            {isStale
              ? "Script changed since last parse. Run Parse again before saving."
              : "Parse your script before saving."}
          </AlertDescription>
        </Alert>
      ) : null}

      {pineOverLimit ? (
        <Alert variant="destructive" className={STRATEGY_PINE_ALERT_CLASS}>
          <StrategyPineAlertIcon tone="error" />
          <AlertDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
            {PINE_SCRIPT_MAX_CHARS_MESSAGE}
          </AlertDescription>
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="destructive" className={STRATEGY_PINE_ALERT_CLASS}>
          <StrategyPineAlertIcon tone="error" />
          <AlertDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>{error}</AlertDescription>
        </Alert>
      ) : null}

      {isStale && result ? (
        <Alert variant="info" className={STRATEGY_PINE_ALERT_CLASS}>
          <StrategyPineAlertIcon tone="info" />
          <AlertDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
            Results below are from your last parse and may not match the current script.
          </AlertDescription>
        </Alert>
      ) : null}

      {showParsePanels || showSavedOnlyHints ? (
        <Box minW={0} w="100%">
          {showParsedUi ? (
            brandFields ? (
              <StrategyPineParseSummaryAlert
                fields={displayFields}
                issues={parseIssues}
                parseStatusUx={requirements?.parse_status_ux}
                injectReadinessUx={requirements?.inject_readiness_ux}
                exitHookReadinessUx={requirements?.exit_hook_readiness_ux}
              />
            ) : (
              <>
                <Box mb={2}>
                  <StrategyPineParseStatusBanner
                    status={displayFields.pine_parse_status}
                    parseStatusUx={requirements?.parse_status_ux}
                    textColor={textColor}
                  />
                </Box>
                <StrategyPineStatusBadges
                  fields={displayFields}
                  subtleText={subtleText}
                  parseStatusUx={requirements?.parse_status_ux}
                  injectReadinessUx={requirements?.inject_readiness_ux}
                  exitHookReadinessUx={requirements?.exit_hook_readiness_ux}
                />
              </>
            )
          ) : (
            <StrategyPineStatusBadges
              fields={displayFields}
              subtleText={subtleText}
              parseStatusUx={requirements?.parse_status_ux}
              injectReadinessUx={requirements?.inject_readiness_ux}
              exitHookReadinessUx={requirements?.exit_hook_readiness_ux}
            />
          )}

          {showParsedUi ? (
            <>
              {!brandFields ? (
                <Box mt={2}>
                  <StrategyPineInjectSummary
                    injectReadiness={displayFields.inject_readiness}
                    injectReadinessUx={requirements?.inject_readiness_ux}
                  />
                </Box>
              ) : null}

              {parseIssues.length > 0 && !brandFields ? (
                <Box mt={3}>
                  <StrategyPineParseIssuesAlert issues={parseIssues} subtleText={subtleText} />
                  <StrategyPineParseIssues
                    issues={parseIssues}
                    subtleText={subtleText}
                    textColor={textColor}
                    borderColor={borderColor}
                    onGoToLine={goToLine}
                  />
                </Box>
              ) : null}

              {showSuccessPath ? (
                <VStack align="stretch" spacing={3} mt={3} minW={0} w="100%">
                  <StrategyPineExecutionModelCallout
                    executionModel={pineIr?.execution_model}
                    subtleText={subtleText}
                    textColor={textColor}
                    borderColor={borderColor}
                  />
                  {entrySites.length > 0 ? (
                    <StrategyPineEntryConditions
                      entrySites={entrySites}
                      guardContextUx={requirements?.guard_context_ux}
                      subtleText={subtleText}
                      textColor={textColor}
                      borderColor={borderColor}
                      onGoToLine={goToLine}
                    />
                  ) : null}
                  <StrategyPineExitHooks
                    exitHookReadiness={displayFields.exit_hook_readiness}
                    exitSites={exitSites}
                    subtleText={subtleText}
                    textColor={textColor}
                    borderColor={borderColor}
                  />
                </VStack>
              ) : result?.pine_parse_status === "failed" ? (
                <Text fontSize="sm" color={subtleText} mt={2}>
                  No entry structure was extracted. Fix the issues above and parse again.
                </Text>
              ) : null}
            </>
          ) : showSavedOnlyHints ? (
            <Text fontSize="xs" color={subtleText} mt={2}>
              Click Parse to load issues, entry conditions, and merge readiness for the current script.
            </Text>
          ) : null}
        </Box>
      ) : null}
    </VStack>
  );
}
